import { useBookkeepingBankAccounts } from '@features/accounts/hooks/useBookkeepingBankAccounts'
import { useFetchAccounts } from '@features/accounts/hooks/useFetchAccounts'
import { useHasBookkeepingAccountData } from '@features/accounts/hooks/useHasBookkeepingAccountData'
import { useOpenBankingAccounts } from '@features/openBanking/hooks/useOpenBankingAccounts'

import { BankAccountSource } from '../enums/bankAccountSource'
import { useFetchBankAccounts } from './useFetchBankAccounts'

interface UseShouldShowBankingOnboardingResponse {
  isLoading: boolean
  shouldShowBankingOnboarding: boolean
}

/**
 * Whether the banking onboarding empty state should replace the Accounts and Transactions views: the
 * organization has not set up banking yet. Deleted open banking connections and archived accounts still count
 * as set up, and the default bookkeeping account we create for every organization (source BILLY) is ignored
 * unless it is the only account and already holds transactions. More than one bookkeeping bank account also
 * counts as set up.
 */
export const useShouldShowBankingOnboarding = (): UseShouldShowBankingOnboardingResponse => {
  const { bookkeepingBankAccounts, isLoading: isLoadingBookkeepingAccounts } = useBookkeepingBankAccounts()
  const { isError: hasAccountsError, meta: accountsMeta } = useFetchAccounts()
  const { bankAccounts, isError: hasBankAccountsError, isLoading: isLoadingBankAccounts } = useFetchBankAccounts()
  const {
    openBankingAccounts,
    isError: hasOpenBankingAccountsError,
    isLoading: isLoadingOpenBankingAccounts,
  } = useOpenBankingAccounts({
    withDeleted: true,
  })

  const singleBookkeepingAccount = bookkeepingBankAccounts.length === 1 ? bookkeepingBankAccounts[0] : undefined
  const {
    bookkeepingAccountHasExistingData,
    isError: hasAccountDataError,
    isLoading: isLoadingAccountData,
  } = useHasBookkeepingAccountData({
    selectedBookkeepingAccount: singleBookkeepingAccount,
  })

  // The bootstrap endpoint seeds the accounts cache with a partial subset that has no `meta`, so wait for the real
  // response before judging how many bank accounts the organization has
  const isLoadingAccounts = isLoadingBookkeepingAccounts || (!accountsMeta && !hasAccountsError)
  const isLoading = isLoadingAccounts || isLoadingBankAccounts || isLoadingOpenBankingAccounts || isLoadingAccountData

  // More than one account, or a single account that already holds transactions, means the organization
  // has already set up its banking
  const hasEverConnected =
    bookkeepingBankAccounts.length > 1 ||
    !!bookkeepingAccountHasExistingData ||
    !!openBankingAccounts?.length ||
    bankAccounts.some(({ source }) => source !== BankAccountSource.BILLY) ||
    bookkeepingBankAccounts.some(({ name }) => name?.toLowerCase() === 'kontist')

  // A failed request means we can't tell whether banking is set up, so don't risk hiding it from a connected user
  const hasLoadingError = hasAccountsError || hasAccountDataError || hasBankAccountsError || hasOpenBankingAccountsError

  return { isLoading, shouldShowBankingOnboarding: !isLoading && !hasLoadingError && !hasEverConnected }
}
